import {z} from 'zod';

import {
  BasePriceSchema,
  PriceWithAndWithoutVatSchema,
  BaseVoidResponseSchema,
} from '@dms/api/shared';

const ArticlePriceIndicatorSchema = z.enum(['EQUAL', 'HIGHER', 'LOWER', 'NO_PRICE']);
export type ArticlePriceIndicator = z.infer<typeof ArticlePriceIndicatorSchema>;

/**
 * GET
 */
export const GetDirectSaleMaterialItemRequestSchema = z.object({
  directSaleId: z.string(),
  itemId: z.string(),
});
export type GetDirectSaleMaterialItemRequest = z.infer<
  typeof GetDirectSaleMaterialItemRequestSchema
>;

export const WarehouseSchema = z.object({
  warehouseId: z.string(),
  warehouseArticleId: z.string(),
  storageLocation: z.string().optional(),
  discountGroup: z.string().optional(),
  warehouseAction: z.string(),
});

export const QuantitySchema = z.object({
  amount: z.number(),
  unit: z.string(),
});

export const VatDetailsSchema = z.object({
  type: z.string(),
  freeCode: z.string().optional(),
});

export const DiscountSourceTypeSchema = z.enum([
  'customer-contract',
  'custom-discount',
  'manual-discount',
]);
export type DiscountSourceType = z.infer<typeof DiscountSourceTypeSchema>;

export const DiscountSchema = z.object({
  isDoNotApplyDiscount: z.boolean(),
  isDiscountEnable: z.boolean(),
  discountSourceType: DiscountSourceTypeSchema,
  discountRate: z.number(),
});
export type Discount = z.infer<typeof DiscountSchema>;

export const AvailableDiscountSchema = z.object({
  discountSourceType: DiscountSourceTypeSchema,
  discountSourceTypeName: z.string(),
  discountRate: z.number(),
  isSet: z.boolean(),
  isChangeable: z.boolean(),
});

export const ItemsDiscountsSchema = z.object({
  isDoNotApplyDiscount: z.boolean(),
  isDiscountEnable: z.boolean(),
  availableDiscounts: z.array(AvailableDiscountSchema).optional(),
});
export type ItemsDiscountsMetadaWarehouseDirectSaleItemSparePart = z.infer<
  typeof ItemsDiscountsSchema
>;

export const MarginSchema = PriceWithAndWithoutVatSchema.merge(
  z.object({
    marginPercentage: z.number().nullable(),
    isMarginUnderMinimum: z.boolean(),
    isMarginExisting: z.boolean(),
  })
);

export const GetDirectSaleMaterialItemResponseSchema = z.object({
  id: z.string(),
  serialNumber: z.number().nullish(),
  warehouse: WarehouseSchema,
  name: z.string(),
  nameForInvoice: z.string().nullish(),
  number: z.string(),
  neededAt: z.string().optional(),
  quantity: QuantitySchema,
  dispensingUnit: z.number(),
  isUnitPriceWithVat: z.boolean(),
  purchasePricePerUnit: BasePriceSchema,
  sellingPricePerUnit: BasePriceSchema,
  sellingPriceVat: VatDetailsSchema,
  articlePrice: PriceWithAndWithoutVatSchema,
  articlePriceIndicator: ArticlePriceIndicatorSchema,
  assignMechanic: z.string().optional(),
  isCustomPrice: z.boolean(),
  baseSellingPricePerUnit: BasePriceSchema,
  baseSellingPriceVat: z.object({type: z.string()}),
  discountSource: z.string().optional(),
  manufacturerNumber: z.string(),
  defaultSupplierId: z.string().nullish(),
  defaultSupplierDiscountGroup: z.string().nullish(),
  manufacturerId: z.string(),
  isAllowedItemEditing: z.boolean(),
  isAllowedQuantityEditing: z.boolean(),
  workItemGroupId: z.string().nullish(),
  itemsDiscounts: ItemsDiscountsSchema,
  sellingTotalPrice: PriceWithAndWithoutVatSchema,
  purchaseTotalPrice: PriceWithAndWithoutVatSchema,
  marginTotal: MarginSchema,
  marginPerUnit: MarginSchema,
  discount: BasePriceSchema,
  isOneTimeSale: z.boolean(),
  minMaxQuantity: z
    .object({
      minQuantity: z.number().nullish(),
      maxQuantity: z.number().nullish(),
    })
    .optional(),
});
export type GetDirectSaleMaterialItemResponse = z.infer<
  typeof GetDirectSaleMaterialItemResponseSchema
>;

export const GetDirectSaleMaterialItemCalculatePriceRequestBodySchema = z.object({
  quantity: z.number().nullish(),
  sellingPricePerUnit: z.number().nullish(),
  vatType: z.string().nullish(),
  isCustomPrice: z.boolean().nullish(),
  discount: z.object({
    isDoNotApplyDiscount: z.boolean().nullish(),
    isDiscountEnable: z.boolean().nullish(),
    discountSourceType: DiscountSourceTypeSchema.nullish(),
    discountRate: z.number().nullish(),
  }),
  getCurrentPrice: z.boolean(),
});

export const GetDirectSaleMaterialItemCalculatePriceRequestSchema = z.object({
  directSaleId: z.string(),
  itemId: z.string(),
  body: GetDirectSaleMaterialItemCalculatePriceRequestBodySchema,
});
export type GetDirectSaleMaterialItemCalculatePriceRequest = z.infer<
  typeof GetDirectSaleMaterialItemCalculatePriceRequestSchema
>;

const MaterialRecalculatedPricesSchema = z.object({
  articlePrice: PriceWithAndWithoutVatSchema,
  articlePriceIndicator: ArticlePriceIndicatorSchema,
  sellingPricePerUnit: BasePriceSchema,
  sellingTotalPrice: PriceWithAndWithoutVatSchema,
  purchaseTotalPrice: PriceWithAndWithoutVatSchema,
  marginTotal: MarginSchema,
  marginPerUnit: MarginSchema,
  discount: BasePriceSchema,
  isCustomPrice: z.boolean(),
  itemsDiscounts: ItemsDiscountsSchema,
});

export const GetDirectSaleMaterialItemCalculatePriceResponseSchema =
  MaterialRecalculatedPricesSchema;
export type GetDirectSaleMaterialItemCalculatePriceResponse = z.infer<
  typeof GetDirectSaleMaterialItemCalculatePriceResponseSchema
>;

/**
 * POST
 */

/**
 * PATCH
 */
export const PatchDirectSaleMaterialItemRequestBodySchema = z.object({
  /** Stored as the invoice name only. */
  name: z.string().optional(),
  quantity: z.number(),
  isUnitPriceWithVat: z.boolean(),
  sellingPricePerUnit: z.number(),
  vatType: z.string(),
  vatFreeCode: z.string().nullish(),
  mechanic: z.string().nullish(),
  isCustomPrice: z.boolean(),
  neededAt: z.string().nullish(),
  workItemGroupId: z.string().nullish(),
  discount: z.object({
    isDoNotApplyDiscount: z.boolean().nullish(),
    isDiscountEnable: z.boolean(),
    discountSourceType: DiscountSourceTypeSchema.nullish(),
    discountRate: z.number().nullish(),
  }),
  isOneTimeSale: z.boolean(),
});

export const PatchDirectSaleMaterialItemRequestSchema = z.object({
  directSaleId: z.string(),
  itemId: z.string(),
  body: PatchDirectSaleMaterialItemRequestBodySchema,
});
export type PatchDirectSaleMaterialItemRequest = z.infer<
  typeof PatchDirectSaleMaterialItemRequestSchema
>;

export const PatchDirectSaleMaterialItemResponseSchema = BaseVoidResponseSchema;
export type PatchDirectSaleMaterialItemResponse = z.infer<
  typeof PatchDirectSaleMaterialItemResponseSchema
>;
